import { FC } from "react";
import SkeletonBox from "src/components/SkeletonBox/SkeletonBox";

const ActionCardSkeleton: FC<{ withIcon?: boolean }> = ({ withIcon }) => (
  <div className="flex flex-1 items-center justify-between gap-3 rounded-md bg-background-minimal px-4 py-3">
    <div className="flex min-w-0 items-center gap-3">
      {withIcon && <SkeletonBox className="size-10 shrink-0 rounded-md" />}
      <div className="flex min-w-0 flex-col gap-1">
        <SkeletonBox className="h-4 w-20" />
        <SkeletonBox className="h-6 w-28" />
      </div>
    </div>
    {!withIcon && <SkeletonBox className="h-5 w-14 shrink-0" />}
  </div>
);

const MainDetailsSkeleton = () => (
  <div className="flex w-full flex-col gap-4 rounded-sm bg-background-default p-5 outline outline-border-default">
    <div className="flex items-start justify-between">
      <div className="flex flex-col gap-0.5">
        <SkeletonBox className="h-8 w-56 max-w-full" />
        <SkeletonBox className="h-5 w-40 max-w-full" />
      </div>
      <SkeletonBox className="h-7 w-24 shrink-0 rounded-full" />
    </div>

    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3 rounded-md border border-dashed border-border-primary bg-background-primary-lighter px-4 py-3">
        <div className="flex min-w-0 flex-col gap-1">
          <SkeletonBox className="h-4 w-24" />
          <SkeletonBox className="h-7 w-48 max-w-full md:h-8" />
        </div>
        <SkeletonBox className="h-9 w-12 shrink-0 rounded-md sm:w-32" />
      </div>
      <div className="flex flex-col gap-3 md:flex-row">
        <ActionCardSkeleton />
        <ActionCardSkeleton withIcon />
      </div>
    </div>

    <div className="border-t border-dashed border-border-default pt-4">
      <div className="grid grid-cols-2 gap-x-4 gap-y-5 md:grid-cols-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex flex-col gap-0.5">
            <SkeletonBox className="h-4 w-20" />
            <SkeletonBox className="h-5 w-24" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const InstructionsSkeleton = () => (
  <>
    <div className="hidden w-full flex-col rounded-xs bg-background-default shadow-sm md:flex">
      <div className="flex w-full border-b border-border-subtle px-5">
        {[0, 1].map((i) => (
          <div key={i} className="flex flex-1 items-center justify-center gap-3 p-4">
            <SkeletonBox className="h-10 w-10 shrink-0 rounded-full" />
            <SkeletonBox className="h-5 w-20" />
          </div>
        ))}
      </div>
      <div className="flex w-full flex-col gap-2 px-3 py-2">
        <div className="flex flex-col gap-2 p-2">
          <SkeletonBox className="h-3 w-full" />
          <SkeletonBox className="h-3 w-full" />
          <SkeletonBox className="h-3 w-5/6" />
          <SkeletonBox className="h-3 w-2/3" />
        </div>
      </div>
    </div>
    <div className="flex w-full flex-col gap-3 md:hidden">
      {[0, 1].map((i) => (
        <div key={i} className="flex w-full items-center gap-3 rounded-xl bg-background-default p-4 outline outline-border-subtle">
          <SkeletonBox className="h-10 w-10 shrink-0 rounded-full" />
          <SkeletonBox className="h-5 flex-1" />
          <SkeletonBox className="h-5 w-5 shrink-0" />
        </div>
      ))}
    </div>
  </>
);

export default function DiscountDetailsSkeleton() {
  return (
    <div className="flex w-full flex-col gap-3 pb-5 md:flex-row md:p-5">
      <div className="flex flex-5">
        <MainDetailsSkeleton />
      </div>
      <div className="flex flex-2">
        <InstructionsSkeleton />
      </div>
    </div>
  );
}
